import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, LogOut, Menu, X, BookOpen, Settings } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import "../styles/layout.css";

function getInitials(name?: string): string {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0][0].toUpperCase();
}

export default function DashboardLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    const closeSidebar = () => setSidebarOpen(false);

    return (
        <div className="layout">
            {/* Topbar */}
            <header className="topbar">
                <div className="topbar-left">
                    <button
                        className="topbar-hamburger"
                        onClick={() => setSidebarOpen((v) => !v)}
                        aria-label="Toggle sidebar"
                    >
                        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
                    </button>
                    <span className="topbar-brand">RMT-AI</span>
                </div>

                <div className="topbar-right">
                    <div className="topbar-user">
                        <div className="topbar-avatar">
                            {getInitials(user?.fullname)}
                        </div>
                        <div className="topbar-user-info">
                            <span className="topbar-user-name">{user?.fullname}</span>
                            <span className="topbar-user-email">{user?.email}</span>
                        </div>
                    </div>
                    <div className="topbar-divider" />
                    <button className="topbar-logout" onClick={handleLogout}>
                        <LogOut size={14} />
                        Logout
                    </button>
                </div>
            </header>

            {/* Sidebar */}
            <nav className={`sidebar${sidebarOpen ? " open" : ""}`}>
                <div className="sidebar-label">Menu</div>
                <div className="sidebar-nav">
                    <NavLink
                        to="/dashboard"
                        end
                        className={({ isActive }) =>
                            `sidebar-link${isActive ? " active" : ""}`
                        }
                        onClick={closeSidebar}
                    >
                        <span className="sidebar-icon"><LayoutDashboard size={18} /></span>
                        Dashboard
                    </NavLink>
                    <NavLink
                        to="/clients"
                        className={({ isActive }) =>
                            `sidebar-link${isActive ? " active" : ""}`
                        }
                        onClick={closeSidebar}
                    >
                        <span className="sidebar-icon"><Users size={18} /></span>
                        Clients
                    </NavLink>
                    {user?.role === "admin" && (
                        <NavLink
                            to="/admin/knowledge"
                            className={({ isActive }) =>
                                `sidebar-link${isActive ? " active" : ""}`
                            }
                            onClick={closeSidebar}
                        >
                            <span className="sidebar-icon"><BookOpen size={18} /></span>
                            Global Knowledge
                        </NavLink>
                    )}
                    <NavLink
                        to="/settings"
                        className={({ isActive }) =>
                            `sidebar-link${isActive ? " active" : ""}`
                        }
                        onClick={closeSidebar}
                    >
                        <span className="sidebar-icon"><Settings size={18} /></span>
                        Settings
                    </NavLink>
                </div>
            </nav>

            {/* Mobile overlay */}
            <div
                className={`sidebar-overlay${sidebarOpen ? " open" : ""}`}
                onClick={closeSidebar}
            />

            {/* Page content */}
            <main className="layout-content">
                <Outlet />
            </main>
        </div>
    );
}
